import React, { useEffect, useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Form, Input } from 'antd';
import { ButtonTheme, ModalTheme } from '../../../components';
import { ContainerButton } from '../../../styles/global-style';
import {
  LINE_MESSAGE_PLACEHOLDERS,
  LINE_MESSAGE_SAMPLE_RECORD,
  renderClaimMessage,
} from '../../cliam/shared/lineMessage';
import { useInsertLineMessageTemplate } from '../hooks/useInsertLineMessageTemplate';
import { useUpdateLineMessageTemplate } from '../hooks/useUpdateLineMessageTemplate';

const NAME_MAX_LENGTH = 100;
const TEXT_MAX_LENGTH = 5000;

const PlaceholderLegend = ({ onInsert }) => (
  <div style={{ border: '1px solid #f0f0f0', borderRadius: 4, padding: 12 }}>
    <div style={{ fontWeight: 600, marginBottom: 8 }}>แทรกข้อมูลอัตโนมัติ (คลิกเพื่อแทรก)</div>
    {LINE_MESSAGE_PLACEHOLDERS.map((placeholder) => (
      <div key={placeholder.token} style={{ marginBottom: 6 }}>
        <a onClick={() => onInsert(placeholder.token)}>{placeholder.token}</a>
        <div style={{ color: '#999', fontSize: 12 }}>
          {placeholder.description} เช่น {placeholder.example}
        </div>
      </div>
    ))}
  </div>
);

export const TemplateFormModal = ({ open, onToggle, onFetch, initialValues }) => {
  const isEdit = !!initialValues?.templateId;
  const { control, setValue, getValues, handleSubmit, watch } = useForm({
    defaultValues: { templateName: '', templateText: '' },
  });
  const textAreaRef = useRef(null);
  const templateText = watch('templateText') || '';

  useEffect(() => {
    setValue('templateName', initialValues?.templateName || '');
    setValue('templateText', initialValues?.templateText || '');
  }, [initialValues]);

  const { mutate: insertTemplate, isPending: isInserting } = useInsertLineMessageTemplate({
    onSuccessCallBack: onFetch,
  });
  const { mutate: updateTemplate, isPending: isUpdating } = useUpdateLineMessageTemplate({
    onSuccessCallBack: onFetch,
  });

  // แทรก token ที่ตำแหน่ง cursor ปัจจุบันของ textarea ไม่ใช่ต่อท้ายข้อความเสมอ
  const insertToken = (token) => {
    const textarea = textAreaRef.current?.resizableTextArea?.textArea;
    const current = getValues('templateText') || '';
    if (!textarea) {
      setValue('templateText', `${current}${token}`);
      return;
    }
    const start = textarea.selectionStart ?? current.length;
    const end = textarea.selectionEnd ?? current.length;
    const next = `${current.slice(0, start)}${token}${current.slice(end)}`;
    setValue('templateText', next);
    requestAnimationFrame(() => {
      textarea.focus();
      const cursor = start + token.length;
      textarea.setSelectionRange(cursor, cursor);
    });
  };

  const onSubmit = (values) => {
    const payload = {
      templateName: values.templateName.trim(),
      templateText: values.templateText,
    };
    const onSuccess = () => onToggle({ defaultValues: '' });
    if (isEdit) {
      updateTemplate({ templateId: initialValues.templateId, ...payload }, { onSuccess });
    } else {
      insertTemplate(payload, { onSuccess });
    }
  };

  return (
    <ModalTheme
      title={isEdit ? 'แก้ไข template' : 'เพิ่ม template'}
      open={open}
      onToggle={onToggle}
      width={760}
    >
      <Controller
        name="templateName"
        control={control}
        rules={{
          validate: (value) => !!value.trim() || 'กรุณาระบุชื่อ template',
          maxLength: {
            value: NAME_MAX_LENGTH,
            message: `ระบุได้ไม่เกิน ${NAME_MAX_LENGTH} ตัวอักษร`,
          },
        }}
        render={({ field, fieldState }) => (
          <Form.Item
            label="ชื่อ template"
            required
            validateStatus={fieldState.error ? 'error' : undefined}
            help={fieldState.error?.message}
          >
            <Input {...field} maxLength={NAME_MAX_LENGTH} showCount placeholder="ชื่อ template" />
          </Form.Item>
        )}
      />

      <div style={{ display: 'flex', gap: 16 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Controller
            name="templateText"
            control={control}
            rules={{
              validate: (value) => !!value.trim() || 'กรุณาระบุข้อความ',
              maxLength: {
                value: TEXT_MAX_LENGTH,
                message: `ระบุได้ไม่เกิน ${TEXT_MAX_LENGTH} ตัวอักษร`,
              },
            }}
            render={({ field, fieldState }) => (
              <Form.Item
                label="ข้อความ"
                required
                validateStatus={fieldState.error ? 'error' : undefined}
                help={fieldState.error?.message}
              >
                <Input.TextArea
                  {...field}
                  ref={textAreaRef}
                  rows={8}
                  maxLength={TEXT_MAX_LENGTH}
                  showCount
                />
              </Form.Item>
            )}
          />
          <div style={{ fontWeight: 600, marginBottom: 8 }}>ตัวอย่างข้อความ</div>
          <div
            style={{ padding: 8, background: '#fafafa', borderRadius: 4, whiteSpace: 'pre-wrap' }}
          >
            {renderClaimMessage(templateText, LINE_MESSAGE_SAMPLE_RECORD)}
          </div>
        </div>
        <div style={{ width: 260, flexShrink: 0 }}>
          <PlaceholderLegend onInsert={insertToken} />
        </div>
      </div>

      <ContainerButton right style={{ marginTop: 16 }}>
        <ButtonTheme
          useFor="SUBMIT"
          onClick={handleSubmit(onSubmit)}
          loading={isInserting || isUpdating}
        />
        <ButtonTheme useFor="CANCEL" onClick={onToggle} />
      </ContainerButton>
    </ModalTheme>
  );
};
